import React, { useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, SparkIcon, Text, Tooltip } from '@spark';
import { tipDirectionFor, WALKTHROUGH_STYLE } from '../registry';
import { useCoachMarkPosition } from '../hooks/useCoachMarkPosition';
import { resolveOverlayHost, scrollerViewport, toHostCoords, WALKTHROUGH_SCROLLER_SELECTOR } from '../utils/overlayHost';
import { notchedOutlinePath } from '../utils/notchedOutline';
import './CoachMark.css';

/** Tour chrome: the only way past a step is the control it points at, or this link. */
export const COACH_MARK_SKIP_LABEL = 'Skip this step';
/** Guide chrome (Figma 9496:15506): `Skip` / `Next`, `Got it` on the last step, `X` closes the whole guide. */
export const COACH_MARK_GUIDE_SKIP_LABEL = 'Skip';
export const COACH_MARK_NEXT_LABEL = 'Next';
export const COACH_MARK_DONE_LABEL = 'Got it';
export const COACH_MARK_CLOSE_LABEL = 'Close guided tour';
/** How far the highlight outline sits outside the anchor's box. */
export const COACH_MARK_HIGHLIGHT_OFFSET = 4;
/** How far the dashed hint sits outside the hinted option's box unless the step says otherwise (Figma 9455-7106). */
export const COACH_MARK_HINT_OFFSET = 4;
/** Gap between the page scroller's top edge and a centered (anchor-less) bubble. */
export const COACH_MARK_CENTERED_TOP_GAP = 16;

const FIXED_HOST = { mode: 'fixed', host: null };

/** Top edge for a centered bubble: just under the navbar, i.e. the page scroller's top. */
const centeredTop = () => {
    const scroller = document.querySelector(WALKTHROUGH_SCROLLER_SELECTOR);
    const top = scroller ? scrollerViewport(scroller).top : 0;
    return Math.round(top + COACH_MARK_CENTERED_TOP_GAP);
};

/**
 * One walkthrough bubble plus the outline that highlights its anchor.
 *
 * Two chromes share the same blue surface:
 * - `tour` (Figma 9430:2632 / 9430:3044): no close control, a `Skip this step`
 *   link and the `Step N/M` counter. The user moves on by using the
 *   highlighted control.
 * - `guide` (Figma 9496:15506): an `X` in the header, the counter on the
 *   left and `Skip` + `Next` (`Got it` on the last step) on the right, so the
 *   user can read along without acting.
 *
 * `centered` guide bubbles (Figma 9607:20688, the task-tour welcome) have no
 * anchor: no outline, no tip, no counter, a single call-to-action button, and
 * they sit horizontally centred right under the navbar.
 *
 * Anchored marks are placed next to `rect` (the anchor's viewport box). A
 * step that asks for one particular option or card also gets a dashed
 * outline around `hintRect` (that element's viewport box), inflated by
 * `hintOffset`.
 * Anchors that scroll with the page scroller get the mark rendered *inside*
 * that scroller, absolutely positioned in its content coordinates, so it
 * scrolls natively with the anchor; everything else (modals, sticky headings)
 * gets a `fixed` mark portaled to the Spark overlay root. See
 * `utils/overlayHost.js`.
 *
 * It is deliberately not modal: no backdrop, no focus trap.
 */
export default function CoachMark({
    variant = WALKTHROUGH_STYLE.tour,
    title,
    body,
    placement = 'bottom',
    align = 'center',
    rect,
    hintRect = null,
    hintOffset = COACH_MARK_HINT_OFFSET,
    stepNumber,
    stepTotal,
    onSkip,
    onNext,
    onClose,
    nextLabel,
    isLast = false,
    centered = false,
    targetElement = null,
    highlight = true,
}) {
    const bubbleRef = useRef(null);
    const titleId = useId();
    const bodyId = useId();
    const hasDocument = typeof document !== 'undefined';
    const guide = variant === WALKTHROUGH_STYLE.guide;
    const { mode, host } = hasDocument && !centered ? resolveOverlayHost(targetElement) : FIXED_HOST;
    const native = mode === 'native';
    // Memoised by value: the position hook keys its re-measure cap on input identity.
    const measured = native ? scrollerViewport(host) : null;
    const viewport = useMemo(
        () => measured,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [measured?.top, measured?.left, measured?.width, measured?.height]
    );
    const position = useCoachMarkPosition({
        bubbleRef,
        rect: centered ? null : rect,
        placement,
        align,
        viewport,
        hintRect,
    });

    if (!hasDocument || (!centered && !rect)) return null;

    const modifier = native ? '--native' : '--fixed';
    const portalHost = host ?? resolveOverlayHost(null).host;
    const resolvedNextLabel = nextLabel ?? (isLast ? COACH_MARK_DONE_LABEL : COACH_MARK_NEXT_LABEL);
    const showCounter = !centered && Number.isFinite(stepNumber) && Number.isFinite(stepTotal);

    const chrome = (
        <>
            <div className="walkthrough-coach-mark__header">
                <Text as="h3" variant="heading-5-semibold" className="walkthrough-coach-mark__title" id={titleId}>
                    {title}
                </Text>
                {guide ? (
                    <button
                        type="button"
                        className="walkthrough-coach-mark__close"
                        aria-label={COACH_MARK_CLOSE_LABEL}
                        onClick={onClose}
                    >
                        <SparkIcon slug="close" size="small" />
                    </button>
                ) : null}
            </div>
            <Text as="p" variant="lead-regular" className="walkthrough-coach-mark__body" id={bodyId}>
                {body}
            </Text>
            {guide ? (
                <div className="walkthrough-coach-mark__footer walkthrough-coach-mark__footer--guide">
                    {showCounter ? (
                        <Text as="span" variant="detail-medium" className="walkthrough-coach-mark__counter">
                            Step {stepNumber}/{stepTotal}
                        </Text>
                    ) : (
                        <span />
                    )}
                    <div className="walkthrough-coach-mark__actions">
                        {!centered && !isLast ? (
                            <button type="button" className="walkthrough-coach-mark__skip-guide" onClick={onSkip}>
                                {COACH_MARK_GUIDE_SKIP_LABEL}
                            </button>
                        ) : null}
                        <Button size="tiny" variant="primary" className="walkthrough-coach-mark__next" onClick={onNext}>
                            {resolvedNextLabel}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="walkthrough-coach-mark__footer">
                    <button type="button" className="walkthrough-coach-mark__skip" onClick={onSkip}>
                        {COACH_MARK_SKIP_LABEL}
                    </button>
                    <Text as="span" variant="detail-medium" className="walkthrough-coach-mark__counter">
                        Step {stepNumber}/{stepTotal}
                    </Text>
                </div>
            )}
        </>
    );

    if (centered) {
        return createPortal(
            <div
                ref={bubbleRef}
                className={`walkthrough-coach-mark walkthrough-coach-mark--fixed walkthrough-coach-mark--centered walkthrough-coach-mark--${variant}`}
                style={{ top: centeredTop() }}
                role="dialog"
                aria-live="polite"
                aria-labelledby={titleId}
                aria-describedby={bodyId}
                data-testid="walkthrough-coach-mark"
                data-placement="center"
            >
                <Tooltip type="slot" variant="dark" tipDirection="no-tip" className="walkthrough-coach-mark__tooltip">
                    {chrome}
                </Tooltip>
            </div>,
            portalHost
        );
    }

    const tipDirection = tipDirectionFor(position.placement, align);
    const place = (point) => (native ? toHostCoords(point, host, viewport) : point);
    const outline = place({ top: rect.top - COACH_MARK_HIGHLIGHT_OFFSET, left: rect.left - COACH_MARK_HIGHLIGHT_OFFSET });
    const inset = hintOffset ?? COACH_MARK_HINT_OFFSET;
    const hintOutline = hintRect ? place({ top: hintRect.top - inset, left: hintRect.left - inset }) : null;
    const bubble = place({ top: position.top, left: position.left });
    // An anchor made of a trigger plus its open panel gets one outline hugging both instead of their bounding box.
    const notchedPath = notchedOutlinePath(rect, COACH_MARK_HIGHLIGHT_OFFSET);

    return createPortal(
        <>
            {highlight ? (
                <div
                    className={`walkthrough-highlight walkthrough-highlight${modifier}${
                        notchedPath ? ' walkthrough-highlight--notched' : ''
                    }`}
                    data-testid="walkthrough-highlight"
                    aria-hidden="true"
                    style={{
                        top: outline.top,
                        left: outline.left,
                        width: rect.width + COACH_MARK_HIGHLIGHT_OFFSET * 2,
                        height: rect.height + COACH_MARK_HIGHLIGHT_OFFSET * 2,
                    }}
                >
                    {notchedPath ? (
                        <svg className="walkthrough-highlight__svg" focusable="false">
                            <path className="walkthrough-highlight__path" d={notchedPath} />
                        </svg>
                    ) : null}
                </div>
            ) : null}
            {hintOutline ? (
                <div
                    className={`walkthrough-hint walkthrough-hint${modifier}`}
                    data-testid="walkthrough-hint"
                    aria-hidden="true"
                    style={{
                        top: hintOutline.top,
                        left: hintOutline.left,
                        width: hintRect.width + inset * 2,
                        height: hintRect.height + inset * 2,
                    }}
                />
            ) : null}
            <div
                ref={bubbleRef}
                className={`walkthrough-coach-mark walkthrough-coach-mark${modifier} walkthrough-coach-mark--${position.placement} walkthrough-coach-mark--${variant}`}
                style={{
                    top: bubble.top,
                    left: bubble.left,
                    visibility: position.ready ? 'visible' : 'hidden',
                    // Beside its anchor, the tip points at the dashed option (or the
                    // anchor) even when the bubble slid to stay inside the viewport.
                    '--walkthrough-coach-mark-tip-top':
                        position.tipOffset == null ? undefined : `${position.tipOffset}px`,
                }}
                role="dialog"
                aria-live="polite"
                aria-labelledby={titleId}
                aria-describedby={bodyId}
                data-testid="walkthrough-coach-mark"
                data-placement={position.placement}
            >
                <Tooltip type="slot" variant="dark" tipDirection={tipDirection} className="walkthrough-coach-mark__tooltip">
                    {chrome}
                </Tooltip>
            </div>
        </>,
        portalHost
    );
}
